import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from './baseQuery';
import { BackendResponse } from '../../types';
import { parseQueryParams } from '../../utils';

export interface WarehouseNodeType {
  id: number;
  name: string;
  createdAt?: string;
}

export interface WarehouseNode {
  id: number;
  code: string;
  name: string;
  nodeType: WarehouseNodeType | string;
  parentId: number | null;
  levelNo?: number;
  isStorageUnit: boolean;
  isActive?: boolean;
  sortOrder: number;
  countryId?: number | null;
  stateId?: number | null;
  addressLine1?: string | null;
  addressLine2?: string | null;
  addressLine3?: string | null;
  pincode?: string | null;
  accessTypeId?: number | null;
  uidRequired?: boolean;
  tempratureControlled?: boolean;
  totalLevel?: number | null;
  totalUnit?: number | null;
  binLevel?: number | null;
  binBay?: number | null;
  noOfUid?: number | null;
  blockForPutaway?: boolean;
  blockForPicking?: boolean;
  children?: WarehouseNode[];
  [key: string]: unknown;
}

export interface NodePayload {
  code: string;
  name: string;
  nodeType: string;
  parentId?: number | null;
  isStorageUnit: boolean;
  sortOrder: number;
  [key: string]: string | number | boolean | null | undefined;
}

export interface BinNode {
  id: number;
  code: string;
  name: string;
  nodeType: WarehouseNodeType | string;
  parentId: number;
  levelNo: number;
  isStorageUnit: boolean;
  isActive: boolean;
  sortOrder: number;
  createdAt?: string;
  updatedAt?: string;
}

export interface BinDetailsResponse {
  data: BinNode[];
  totalBins: number;
  rackName?: string;
  rackId?: number | string;
  parentId?: number | string;
  message: string;
}

export interface ApiResponse<T = unknown> {
  responseCode?: string;
  message?: string;
  responseMessage?: string;
  responseMessageMISC?: string;
  responseDynamic?: unknown;
  data?: T;
  result?: unknown;
}

export interface ReferenceDocumentTypeDropdownOption {
  label: string;
  value: string;
}

export interface WarehouseBinDetailsQuery {
  id: number | string;
  page?: number;
  pageSize?: number;
}

/** Safely extract the nodeType name string from either the new object shape or the legacy string value. */
export const getNodeTypeName = (nodeType: WarehouseNodeType | string | undefined | null): string => {
  if (!nodeType) return '';
  if (typeof nodeType === 'string') return nodeType;
  return nodeType.name ?? '';
};

/**
 * Normalise a nodeType name to the uppercase-underscore key format used for
 * style lookups and comparisons (e.g. "Storage Location" → "STORAGE_LOCATION").
 */
export const normaliseNodeType = (nodeType: WarehouseNodeType | string | undefined | null): string =>
  getNodeTypeName(nodeType).trim().toUpperCase().replace(/\s+/g, '_');

const nodePayload = (data: Partial<NodePayload>): NodePayload => {
  const payload: NodePayload = {
    code: data?.code || '',
    name: data?.name || '',
    nodeType: data?.nodeType || '',
    parentId: data?.parentId ?? null,
    isStorageUnit: data?.isStorageUnit ?? false,
    sortOrder: data?.sortOrder ?? 3,
  };

  Object.entries(data).forEach(([key, value]) => {
    if (
      ![
        'code',
        'name',
        'nodeType',
        'parentId',
        'isStorageUnit',
        'sortOrder',
      ].includes(key) &&
      value !== '' &&
      value !== undefined
    ) {
      payload[key] = value;
    }
  });

  return payload;
};

const warehouseApi = createApi({
  reducerPath: 'warehouse',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['WarehouseHierarchy', 'WarehouseBins', 'WarehouseMaster', 'AccessType', 'InventoryCategory', 'VehicleType', 'WarehouseUniversalFieldSet', 'TransactionType', 'TransactionSubType',
    'TransactionCompanyMapping', 'WarehouseFormFieldMapping', 'WarehouseTransaction', 'UserNodeMapping', 'NumberRange', 'BatchNumberRange', 'SerialNumberRange', 'WaveRelease', 'NodeType', 'BinCapacityMapping', 'Asset', 'ZoneGroupMapping', 'WarehouseItemMapping'],
  endpoints: builder => ({
    getWarehouseMaster: builder.query<BackendResponse<{ count?: number; list?: any[] } | any[]>, any>({
      query: query => ({
        url: `/warehouse-master${parseQueryParams(query)}`,
        method: 'GET',
      }),
      providesTags: ['WarehouseMaster'],
    }),
    getWarehouseDropdown: builder.query<BackendResponse<{ count?: number; list?: any[] } | any[]>, any>({
      query: query => ({
        url: `/warehouse${parseQueryParams(query)}`,
        method: 'GET',
      }),
      providesTags: ['WarehouseMaster'],
    }),
    getWarehouseListOnUser: builder.query<BackendResponse<{ count?: number; list?: any[] } | any[]>, any>({
      query: query => ({
        url: `/warehouse/warehouse-list-on-user${parseQueryParams(query)}`,
        method: 'GET',
      }),
      providesTags: ['WarehouseMaster'],
    }),
    getWarehouseHierarchy: builder.query<ApiResponse, void>({
      query: () => ({
        url: 'warehouse/hierarchy/view',
        method: 'GET',
      }),
      providesTags: ['WarehouseHierarchy'],
    }),
    createWarehouseNode: builder.mutation<
      ApiResponse<NodePayload>,
      Partial<NodePayload>
    >({
      query: body => ({
        url: 'warehouse/node',
        method: 'POST',
        body: nodePayload(body),
      }),
      invalidatesTags: ['WarehouseHierarchy', 'WarehouseBins'],
    }),
    uploadWarehouseNodesCsv: builder.mutation<ApiResponse, FormData>({
      query: body => ({
        url: 'warehouse/node/upload-csv',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['WarehouseHierarchy', 'WarehouseBins'],
    }),
    downloadWarehouseStructureSample: builder.query<Blob, void>({
      query: () => ({
        url: 'warehouse/node/sample-xlsx',
        method: 'GET',
        responseHandler: response => response.blob(),
      }),
      transformResponse: response => {
        if (response instanceof Blob) return response;
        throw new Error('Unexpected response type');
      },
    }),
    deleteWarehouseNode: builder.mutation<ApiResponse, number | string>({
      query: id => ({
        url: `warehouse/node/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['WarehouseHierarchy', 'WarehouseBins'],
    }),
    updateWarehouseNode: builder.mutation<
      ApiResponse<NodePayload>,
      { id: number | string; body: Partial<NodePayload> }
    >({
      query: ({ id, body }) => ({
        url: `warehouse/node/${id}`,
        method: 'PATCH',
        body: nodePayload(body),
      }),
      invalidatesTags: ['WarehouseHierarchy', 'WarehouseBins'],
    }),
    getWarehouseBinDetails: builder.query<
      ApiResponse<BinDetailsResponse>,
      WarehouseBinDetailsQuery
    >({
      query: ({ id, page, pageSize }) => ({
        url: `warehouse/children/${id}${parseQueryParams({ page, pageSize })}`,
        method: 'GET',
      }),
      providesTags: (_result, _error, { id }) => [{ type: 'WarehouseBins', id }],
    }),

    // ================= ACCESS TYPE =================
    getAccessTypeField: builder.query<BackendResponse<{ count: number; list: any[] }>, any>({
      query: query => ({
        url: `/warehouse-master/access-type${parseQueryParams(query)}`,
        method: 'GET',
      }),
      providesTags: ['AccessType'],
    }),

    createAccessTypeField: builder.mutation<BackendResponse<any>, any>({
      query: payload => ({
        url: `/warehouse-master/access-type`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: (_, error) => (error ? [] : ['AccessType']),
    }),

    updateAccessTypeField: builder.mutation<BackendResponse<void>, { id: number;[key: string]: any }>({
      query: ({ id, ...rest }) => ({
        url: `/warehouse-master/access-type/${id}`,
        method: 'PATCH',
        body: rest,
      }),
      invalidatesTags: (_, error) => (error ? [] : ['AccessType']),
    }),

    deleteAccessTypeField: builder.mutation<BackendResponse<void>, number[] | string[] | number | string>({
      query: ids => ({
        url: `/warehouse-master/access-type`,
        method: 'DELETE',
        body: { ids },
      }),
      invalidatesTags: (_, error) => (error ? [] : ['AccessType']),
    }),

    // ================= INVENTORY CATEGORY =================
    getInventoryCategoryField: builder.query<BackendResponse<{ count: number; list: any[] }>, any>({
      query: query => ({
        url: `/warehouse-master/inventory-category${parseQueryParams(query)}`,
        method: 'GET',
      }),
      providesTags: ['InventoryCategory'],
    }),

    createInventoryCategoryField: builder.mutation<BackendResponse<any>, any>({
      query: payload => ({
        url: `/warehouse-master/inventory-category`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: (_, error) => (error ? [] : ['InventoryCategory']),
    }),

    updateInventoryCategoryField: builder.mutation<BackendResponse<void>, { id: number;[key: string]: any }>({
      query: ({ id, ...rest }) => ({
        url: `/warehouse-master/inventory-category/${id}`,
        method: 'PATCH',
        body: rest,
      }),
      invalidatesTags: (_, error) => (error ? [] : ['InventoryCategory']),
    }),

    deleteInventoryCategoryField: builder.mutation<BackendResponse<void>, number[] | string[] | number | string>({
      query: ids => ({
        url: `/warehouse-master/inventory-category`,
        method: 'DELETE',
        body: { ids },
      }),
      invalidatesTags: (_, error) => (error ? [] : ['InventoryCategory']),
    }),

    // ================= VEHICLE TYPE =================
    getVehicleTypeField: builder.query<BackendResponse<{ count: number; list: any[] }>, any>({
      query: query => ({
        url: `/warehouse-master/vehicle-type${parseQueryParams(query)}`,
        method: 'GET',
      }),
      providesTags: ['VehicleType'],
    }),

    createVehicleTypeField: builder.mutation<BackendResponse<any>, any>({
      query: payload => ({
        url: `/warehouse-master/vehicle-type`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: (_, error) => (error ? [] : ['VehicleType']),
    }),

    updateVehicleTypeField: builder.mutation<BackendResponse<void>, { id: number;[key: string]: any }>({
      query: ({ id, ...rest }) => ({
        url: `/warehouse-master/vehicle-type/${id}`,
        method: 'PATCH',
        body: rest,
      }),
      invalidatesTags: (_, error) => (error ? [] : ['VehicleType']),
    }),

    deleteVehicleTypeField: builder.mutation<BackendResponse<void>, number[] | string[] | number | string>({
      query: ids => ({
        url: `/warehouse-master/vehicle-type`,
        method: 'DELETE',
        body: { ids },
      }),
      invalidatesTags: (_, error) => (error ? [] : ['VehicleType']),
    }),

    // ================= TRANSACTION TYPE =================
    getTransactionTypeField: builder.query<BackendResponse<{ count: number; list: any[] }>, any>({
      query: query => ({
        url: `/warehouse-master/transaction-type${parseQueryParams(query)}`,
        method: 'GET',
      }),
      providesTags: ['TransactionType'],
    }),

    createTransactionTypeField: builder.mutation<BackendResponse<any>, any>({
      query: payload => ({
        url: `/warehouse-master/transaction-type`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: (_, error) => (error ? [] : ['TransactionType']),
    }),

    updateTransactionTypeField: builder.mutation<BackendResponse<void>, { id: number;[key: string]: any }>({
      query: ({ id, ...rest }) => ({
        url: `/warehouse-master/transaction-type/${id}`,
        method: 'PATCH',
        body: rest,
      }),
      invalidatesTags: (_, error) => (error ? [] : ['TransactionType']),
    }),

    deleteTransactionTypeField: builder.mutation<BackendResponse<void>, number[] | string[] | number | string>({
      query: ids => ({
        url: `/warehouse-master/transaction-type`,
        method: 'DELETE',
        body: { ids },
      }),
      invalidatesTags: (_, error) => (error ? [] : ['TransactionType']),
    }),


    // ================= TRANSACTION SUB TYPE =================
    getTransactionSubTypeField: builder.query<BackendResponse<{ count: number; list: any[] }>, any>({
      query: query => ({
        url: `/warehouse-master/transaction-sub-type${parseQueryParams(query)}`,
        method: 'GET',
      }),
      providesTags: ['TransactionSubType'],
    }),

    getTransactionSubTypeReferenceDocumentTypesDropdown: builder.query<BackendResponse<ReferenceDocumentTypeDropdownOption[]>, void>({
      query: () => ({
        url: `/warehouse-master/reference-document-types-dropdown`,
        method: 'GET',
      }),
      providesTags: ['TransactionSubType'],
    }),

    createTransactionSubTypeField: builder.mutation<BackendResponse<any>, any>({
      query: payload => ({
        url: `/warehouse-master/transaction-sub-type`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: (_, error) => (error ? [] : ['TransactionSubType']),
    }),

    updateTransactionSubTypeField: builder.mutation<BackendResponse<void>, { id: number;[key: string]: any }>({
      query: ({ id, ...rest }) => ({
        url: `/warehouse-master/transaction-sub-type/${id}`,
        method: 'PATCH',
        body: rest,
      }),
      invalidatesTags: (_, error) => (error ? [] : ['TransactionSubType']),
    }),

    deleteTransactionSubTypeField: builder.mutation<BackendResponse<void>, number[] | string[] | number | string>({
      query: ids => ({
        url: `/warehouse-master/transaction-sub-type`,
        method: 'DELETE',
        body: { ids },
      }),
      invalidatesTags: (_, error) => (error ? [] : ['TransactionSubType']),
    }),


    // ================= TRANSACTION COMPANY MAPPING =================
    getTransactionCompanyMappingField: builder.query<BackendResponse<{ count: number; list: any[] }>, any>({
      query: query => ({
        url: `/warehouse-master/transaction-company-mapping${parseQueryParams(query)}`,
        method: 'GET',
      }),
      providesTags: ['TransactionCompanyMapping'],
    }),

    createTransactionCompanyMappingField: builder.mutation<BackendResponse<any>, any>({
      query: payload => ({
        url: `/warehouse-master/transaction-company-mapping`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: (_, error) => (error ? [] : ['TransactionCompanyMapping']),
    }),

    updateTransactionCompanyMappingField: builder.mutation<BackendResponse<void>, { id: number;[key: string]: any }>({
      query: ({ id, ...rest }) => ({
        url: `/warehouse-master/transaction-company-mapping/${id}`,
        method: 'PATCH',
        body: rest,
      }),
      invalidatesTags: (_, error) => (error ? [] : ['TransactionCompanyMapping']),
    }),

    deleteTransactionCompanyMappingField: builder.mutation<BackendResponse<void>, number[] | string[] | number | string>({
      query: ids => ({
        url: `/warehouse-master/transaction-company-mapping`,
        method: 'DELETE',
        body: { ids },
      }),
      invalidatesTags: (_, error) => (error ? [] : ['TransactionCompanyMapping']),
    }),

    // ================= UNIVERSAL FIELD SET =================
    getWarehouseUniversalFields: builder.query<BackendResponse<{ count: number; list: any[] }>, any>({
      query: query => ({
        url: `/warehouse-master/universal-set${parseQueryParams(query)}`,
        method: 'GET',
      }),
      providesTags: ['WarehouseUniversalFieldSet'],
    }),

    createWarehouseUniversalField: builder.mutation<BackendResponse<any>, any>({
      query: payload => ({
        url: `/warehouse-master/universal-set`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: (_, error) => (error ? [] : ['WarehouseUniversalFieldSet']),
    }),

    updateWarehouseUniversalField: builder.mutation<BackendResponse<void>, { id: number;[key: string]: any }>({
      query: ({ id, ...rest }) => ({
        url: `/warehouse-master/universal-set/${id}`,
        method: 'PATCH',
        body: rest,
      }),
      invalidatesTags: (_, error) => (error ? [] : ['WarehouseUniversalFieldSet']),
    }),

    deleteWarehouseUniversalFields: builder.mutation<BackendResponse<void>, number[] | string[] | number | string>({
      query: ids => ({
        url: `/warehouse-master/universal-set`,
        method: 'DELETE',
        body: { ids },
      }),
      invalidatesTags: (_, error) => (error ? [] : ['WarehouseUniversalFieldSet']),
    }),



    // ================= FORM FIELD MAPPING =================
    getWarehouseFormFieldMappings: builder.query<BackendResponse<{ count: number; list: any[] }>, any>({
      query: query => ({
        url: `/warehouse-master/form-field-mappings${parseQueryParams(query)}`,
        method: 'GET',
      }),
      providesTags: ['WarehouseFormFieldMapping'],
    }),

    getWarehouseFormFieldMappingById: builder.query<BackendResponse<any>, number>({
      query: id => ({
        url: `/warehouse-master/form-field-mappings/${id}`,
        method: 'GET',
      }),
      providesTags: (_, __, id) => [{ type: 'WarehouseFormFieldMapping', id }],
    }),

    createWarehouseFormFieldMapping: builder.mutation<BackendResponse<any>, any>({
      query: payload => ({
        url: `/warehouse-master/form-field-mappings`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: (_, error) => (error ? [] : ['WarehouseFormFieldMapping']),
    }),

    createWarehouseFormFieldMappingWithReference: builder.mutation<
      BackendResponse<any>,
      { sourceMappingId: number | string; body: any }
    >({
      query: ({ sourceMappingId, body }) => ({
        url: `/warehouse-master/form-field-mappings/create-with-reference/${sourceMappingId}`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_, error) => (error ? [] : ['WarehouseFormFieldMapping']),
    }),

    updateWarehouseFormFieldMapping: builder.mutation<BackendResponse<void>, { id: number; body: any }>({
      query: ({ id, body }) => ({
        url: `/warehouse-master/form-field-mappings/${id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (_, error) => (error ? [] : ['WarehouseFormFieldMapping']),
    }),

    deleteWarehouseFormFieldMappings: builder.mutation<BackendResponse<void>, { ids: number[] | string[] }>({
      query: ({ ids }) => ({
        url: `/warehouse-master/form-field-mappings`,
        method: 'DELETE',
        body: { ids },
      }),
      invalidatesTags: (_, error) => (error ? [] : ['WarehouseFormFieldMapping']),
    }),

    // ================= WAREHOUSE TRANSACTION =================
    getWarehouseTransactions: builder.query<BackendResponse<{ count: number; list: any[] }>, any>({
      query: query => ({
        url: `/warehouse-transaction${parseQueryParams(query)}`,
        method: 'GET',
      }),
      providesTags: ['WarehouseTransaction'],
    }),

    getWarehouseTransactionById: builder.query<BackendResponse<any>, number | string>({
      query: id => ({
        url: `/warehouse-transaction/${id}`,
        method: 'GET',
      }),
      providesTags: (_, __, id) => [{ type: 'WarehouseTransaction', id }],
    }),

    createWarehouseTransactionDraft: builder.mutation<BackendResponse<any>, any>({
      query: payload => ({
        url: `/warehouse-transaction/save-in-draft`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: (_, error) => (error ? [] : ['WarehouseTransaction']),
    }),

    updateWarehouseTransactionDraft: builder.mutation<BackendResponse<any>, { id: number | string; body: any }>({
      query: ({ id, body }) => ({
        url: `/warehouse-transaction/update-draft/${id}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_, error, arg) =>
        error ? [] : ['WarehouseTransaction', { type: 'WarehouseTransaction', id: arg.id }],
    }),

    updateWarehouseTransactionDraftSilently: builder.mutation<
      BackendResponse<any>,
      { id: number | string; body: any }
    >({
      query: ({ id, body }) => ({
        url: `/warehouse-transaction/update-draft/${id}`,
        method: 'PATCH',
        body,
      }),
    }),

    updateWarehouseTransactionGatePassExit: builder.mutation<
      BackendResponse<any>,
      { id: number | string; body: { vehicleOutDateTime?: string | null; vehicleTat?: number | string | null } }
    >({
      query: ({ id, body }) => ({
        url: `/warehouse-transaction/${id}/gate-pass-exit`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_, error, arg) =>
        error ? [] : ['WarehouseTransaction', { type: 'WarehouseTransaction', id: arg.id }],
    }),

    checkWarehouseTransactionRequestNo: builder.query<
      BackendResponse<any>,
      {
        requestNo: string;
        nodeId?: string | number;
        transactionTypeId?: string | number;
        transactionSubTypeId?: string | number;
      }
    >({
      query: query => ({
        url: `/warehouse-transaction/request-no/check${parseQueryParams(query)}`,
        method: 'GET',
      }),
    }),

    finalSubmitWarehouseTransaction: builder.mutation<
      BackendResponse<any>,
      { id: number | string; body: any }
    >({
      query: ({ id, body }) => ({
        url: `/warehouse-transaction/final-submit/${id}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_, error, arg) =>
        error ? [] : ['WarehouseTransaction', { type: 'WarehouseTransaction', id: arg.id }],
    }),

    deleteWarehouseTransaction: builder.mutation<BackendResponse<void>, number | string>({
      query: id => ({
        url: `/warehouse-transaction/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_, error) => (error ? [] : ['WarehouseTransaction']),
    }),

    getWarehouseTransactionPoDetails: builder.query<BackendResponse<any>, any>({
      query: query => ({
        url: `/warehouse-transaction/po-details${parseQueryParams(query)}`,
        method: 'GET',
      }),
    }),

    searchWarehouseRefDocument: builder.query<
      BackendResponse<any>,
      {
        transactionId: string | number;
        search?: string;
        page?: number;
        pageSize?: number;
      }
    >({
      query: params => ({
        url: `/warehouse-transaction/search-ref-document${parseQueryParams(params)}`,
        method: 'GET',
      }),
    }),

    exportWarehouseRefDocument: builder.query<
      Blob,
      {
        transactionId: string | number;
        search?: string;
      }
    >({
      query: params => ({
        url: `/warehouse-transaction/search-ref-document/export${parseQueryParams(params)}`,
        method: 'GET',
        responseHandler: async response => {
          if (response.ok) return response.blob();

          const errorBody = await response.json().catch(() => ({}));
          throw new Error(errorBody?.message || 'Failed to export reference documents');
        },
      }),
      transformResponse: response => {
        if (response instanceof Blob) return response;
        throw new Error('Unexpected response type');
      },
    }),

    searchWarehouseBatchNo: builder.query<
      BackendResponse<any>,
      {
        slug: string;
        search?: string;
        materialId?: number | string;
        materialCode?: string;
        warehouseId?: number | string;
        nodeId?: number | string;
        plantId?: number | string;
        transactionId?: number | string;
        transactionSubTypeId?: number | string;
        page?: number;
        pageSize?: number;
        limit?: number;
      }
    >({
      query: params => ({
        url: `/warehouse-transaction/search-batch${parseQueryParams(params)}`,
        method: 'GET',
      }),
    }),

    exportWarehouseBatchNo: builder.query<
      Blob,
      {
        slug: string;
        search?: string;
        materialId?: number | string;
        materialCode?: string;
        warehouseId?: number | string;
        nodeId?: number | string;
        plantId?: number | string;
        transactionId?: number | string;
        transactionSubTypeId?: number | string;
      }
    >({
      query: params => ({
        url: `/warehouse-transaction/search-batch/export${parseQueryParams(params)}`,
        method: 'GET',
        responseHandler: async response => {
          if (response.ok) return response.blob();

          const errorBody = await response.json().catch(() => ({}));
          throw new Error(errorBody?.message || 'Failed to export batch data');
        },
      }),
      transformResponse: response => {
        if (response instanceof Blob) return response;
        throw new Error('Unexpected response type');
      },
    }),

    deleteWarehouseTransactionLineItem: builder.mutation<
      BackendResponse<void>,
      { lineItemId: number | string; transactionId: number | string }
    >({
      query: ({ lineItemId }) => ({
        url: `/warehouse-transaction/line-items/${lineItemId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_, error, arg) =>
        error ? [] : ['WarehouseTransaction', { type: 'WarehouseTransaction', id: arg.transactionId }],
    }),

    updateWarehouseTransactionLineItem: builder.mutation<
      BackendResponse<any>,
      { lineItemId: number | string; transactionId: number | string; body: any }
    >({
      query: ({ lineItemId, transactionId, body }) => ({
        url: `/warehouse-transaction/line-items/${lineItemId}${parseQueryParams({ transactionId })}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_, error, arg) =>
        error ? [] : ['WarehouseTransaction', { type: 'WarehouseTransaction', id: arg.transactionId }],
    }),

    generateWarehouseTransactionPutawayProposal: builder.mutation<
      BackendResponse<any>,
      { transactionId: number | string; body: { lineItemIds: Array<number | string> } }
    >({
      query: ({ transactionId, body }) => ({
        url: `/warehouse-transaction/${transactionId}/putaway-proposal`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_, error, arg) =>
        error ? [] : ['WarehouseTransaction', { type: 'WarehouseTransaction', id: arg.transactionId }],
    }),

    updateWarehouseTransactionPutawayProposal: builder.mutation<
      BackendResponse<any>,
      { transactionId: number | string; body: any }
    >({
      query: ({ transactionId, body }) => ({
        url: `/warehouse-transaction/${transactionId}/putaway-proposal`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_, error, arg) =>
        error ? [] : ['WarehouseTransaction', { type: 'WarehouseTransaction', id: arg.transactionId }],
    }),

    confirmWarehouseTransactionPutaway: builder.mutation<
      BackendResponse<any>,
      { transactionId: number | string; body: { lineItemIds: Array<number | string> } }
    >({
      query: ({ transactionId, body }) => ({
        url: `/warehouse-transaction/${transactionId}/confirm-putaway`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_, error, arg) =>
        error ? [] : ['WarehouseTransaction', { type: 'WarehouseTransaction', id: arg.transactionId }],
    }),

    printWarehouseTransactionPutawayProposal: builder.query<Blob, number | string>({
      query: transactionId => ({
        url: `/warehouse-transaction/${transactionId}/putaway-proposal/print`,
        method: 'GET',
        responseHandler: async response => {
          if (response.ok) return response.blob();

          const contentType = response.headers.get('content-type') || '';
          if (contentType.includes('application/json')) {
            const errorBody = await response.json();
            throw new Error(errorBody?.message || 'Failed to print putaway proposal');
          }

          const errorText = await response.text();
          throw new Error(errorText || 'Failed to print putaway proposal');
        },
      }),
    }),

    getWarehouseTransactionPutawayProposal: builder.query<
      BackendResponse<any>,
      number | string | { transactionId: number | string; lineItemId?: number | string }
    >({
      query: arg => {
        const transactionId = typeof arg === 'object' && arg !== null ? arg.transactionId : arg;
        const lineItemId = typeof arg === 'object' && arg !== null ? arg.lineItemId : undefined;
        return {
          url: `/warehouse-transaction/${transactionId}/putaway-proposal`,
          method: 'GET',
          params: lineItemId ? { lineItemId } : undefined,
        };
      },
      providesTags: (_, __, arg) => {
        const transactionId = typeof arg === 'object' && arg !== null ? arg.transactionId : arg;
        return [{ type: 'WarehouseTransaction', id: transactionId }];
      },
    }),

    generateWarehouseTransactionBatchNo: builder.mutation<BackendResponse<any>, any>({
      query: body => ({
        url: `/warehouse-transaction/batch-no/generate`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_, error) => (error ? [] : ['WarehouseTransaction']),
    }),

    generateWarehouseTransactionSerialNo: builder.mutation<BackendResponse<any>, any>({
      query: body => ({
        url: `/warehouse-transaction/serial-no/generate`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_, error) => (error ? [] : ['WarehouseTransaction']),
    }),

    getWarehouseTransactionSerialNo: builder.query<
      BackendResponse<any>,
      number | string | { lineItemId: number | string; transactionId?: number | string; binId?: number | string; nodeId?: number | string; materialId?: number | string }
    >({
      query: arg => {
        if (typeof arg === 'object' && arg !== null) {
          const { lineItemId, ...params } = arg;
          return {
            url: `/warehouse-transaction/serial-no/${lineItemId}${parseQueryParams(params)}`,
            method: 'GET',
          };
        }
        return {
          url: `/warehouse-transaction/serial-no/${arg}`,
          method: 'GET',
        };
      },
    }),

    downloadExternalWarehouseTransactionSerialNoSample: builder.query<
      Blob,
      number | string
    >({
      query: lineItemId => ({
        url: `/warehouse-transaction/serial-no/upload-external/${lineItemId}/sample-file`,
        method: 'GET',
        responseHandler: async response => {
          if (response.ok) return response.blob();

          const contentType = response.headers.get('content-type') || '';
          if (contentType.includes('application/json')) {
            const errorBody = await response.json();
            throw new Error(errorBody?.message || 'Failed to download serial upload sample');
          }

          const errorText = await response.text();
          throw new Error(errorText || 'Failed to download serial upload sample');
        },
      }),
      transformResponse: response => {
        if (response instanceof Blob) return response;
        throw new Error('Unexpected response type');
      },
    }),

    uploadExternalWarehouseTransactionSerialNo: builder.mutation<
      BackendResponse<any>,
      { lineItemId: number | string; body: FormData }
    >({
      query: ({ lineItemId, body }) => ({
        url: `/warehouse-transaction/serial-no/upload-external/${lineItemId}`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_, error) => (error ? [] : ['WarehouseTransaction']),
    }),

    getVendorManagementAll: builder.query<BackendResponse<any>, void>({
      query: () => ({
        url: `/vendor-management/all`,
        method: 'GET',
      }),
    }),

    getContractRegion: builder.query<BackendResponse<any>, void>({
      query: () => ({
        url: `/contract-region`,
        method: 'GET',
      }),
    }),

    getOrderReason: builder.query<BackendResponse<any>, void>({
      query: () => ({
        url: `/master/order-reason?all=true`,
        method: 'GET',
      }),
    }),

    // ================= USER NODE MAPPING =================
    getUserNodeMapping: builder.query<BackendResponse<{ count: number; list: any[] }>, any>({
      query: query => ({
        url: `/warehouse-master/user-node-mapping${parseQueryParams(query)}`,
        method: 'GET',
      }),
      providesTags: ['UserNodeMapping'],
    }),

    createUserNodeMapping: builder.mutation<BackendResponse<any>, any>({
      query: payload => ({
        url: `/warehouse-master/user-node-mapping`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: (_, error) => (error ? [] : ['UserNodeMapping']),
    }),

    updateUserNodeMapping: builder.mutation<BackendResponse<void>, { id: number;[key: string]: any }>({
      query: ({ id, ...rest }) => ({
        url: `/warehouse-master/user-node-mapping/${id}`,
        method: 'PATCH',
        body: rest,
      }),
      invalidatesTags: (_, error) => (error ? [] : ['UserNodeMapping']),
    }),

    deleteUserNodeMapping: builder.mutation<BackendResponse<void>, number[] | string[] | number | string>({
      query: ids => ({
        url: `/warehouse-master/user-node-mapping`,
        method: 'DELETE',
        body: { ids },
      }),
      invalidatesTags: (_, error) => (error ? [] : ['UserNodeMapping']),
    }),

    // ================= NUMBER RANGE =================
    getNumberRange: builder.query<BackendResponse<{ count: number; list: any[] }>, any>({
      query: query => ({
        url: `/warehouse-master/number-range${parseQueryParams(query)}`,
        method: 'GET',
      }),
      providesTags: ['NumberRange'],
    }),

    createNumberRange: builder.mutation<BackendResponse<any>, any>({
      query: payload => ({
        url: `/warehouse-master/number-range`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: (_, error) => (error ? [] : ['NumberRange']),
    }),

    updateNumberRange: builder.mutation<BackendResponse<void>, { id: number;[key: string]: any }>({
      query: ({ id, ...rest }) => ({
        url: `/warehouse-master/number-range/${id}`,
        method: 'PATCH',
        body: rest,
      }),
      invalidatesTags: (_, error) => (error ? [] : ['NumberRange']),
    }),

    deleteNumberRange: builder.mutation<BackendResponse<void>, number[] | string[] | number | string>({
      query: ids => ({
        url: `/warehouse-master/number-range`,
        method: 'DELETE',
        body: { ids },
      }),
      invalidatesTags: (_, error) => (error ? [] : ['NumberRange']),
    }),

    // ================= BATCH NUMBER RANGE =================
    getBatchNumberRange: builder.query<BackendResponse<{ count: number; list: any[] }>, any>({
      query: query => ({
        url: `/warehouse-master/batch-number-range${parseQueryParams(query)}`,
        method: 'GET',
      }),
      providesTags: ['BatchNumberRange'],
    }),

    createBatchNumberRange: builder.mutation<BackendResponse<any>, any>({
      query: payload => ({
        url: `/warehouse-master/batch-number-range`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: (_, error) => (error ? [] : ['BatchNumberRange']),
    }),

    updateBatchNumberRange: builder.mutation<BackendResponse<void>, { id: number;[key: string]: any }>({
      query: ({ id, ...rest }) => ({
        url: `/warehouse-master/batch-number-range/${id}`,
        method: 'PATCH',
        body: rest,
      }),
      invalidatesTags: (_, error) => (error ? [] : ['BatchNumberRange']),
    }),

    deleteBatchNumberRange: builder.mutation<BackendResponse<void>, number[] | string[] | number | string>({
      query: ids => ({
        url: `/warehouse-master/batch-number-range`,
        method: 'DELETE',
        body: { ids },
      }),
      invalidatesTags: (_, error) => (error ? [] : ['BatchNumberRange']),
    }),

    // ================= SERIAL NUMBER RANGE =================
    getSerialNumberRange: builder.query<BackendResponse<{ count: number; list: any[] }>, any>({
      query: query => ({
        url: `/warehouse-master/serial-number-range${parseQueryParams(query)}`,
        method: 'GET',
      }),
      providesTags: ['SerialNumberRange'],
    }),

    createSerialNumberRange: builder.mutation<BackendResponse<any>, any>({
      query: payload => ({
        url: `/warehouse-master/serial-number-range`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: (_, error) => (error ? [] : ['SerialNumberRange']),
    }),

    updateSerialNumberRange: builder.mutation<BackendResponse<void>, { id: number;[key: string]: any }>({
      query: ({ id, ...rest }) => ({
        url: `/warehouse-master/serial-number-range/${id}`,
        method: 'PATCH',
        body: rest,
      }),
      invalidatesTags: (_, error) => (error ? [] : ['SerialNumberRange']),
    }),

    deleteSerialNumberRange: builder.mutation<BackendResponse<void>, number[] | string[] | number | string>({
      query: ids => ({
        url: `/warehouse-master/serial-number-range`,
        method: 'DELETE',
        body: { ids },
      }),
      invalidatesTags: (_, error) => (error ? [] : ['SerialNumberRange']),
    }),

    // ================= WAVE RELEASE =================
    getWaveRelease: builder.query<BackendResponse<{ count: number; list: any[] }>, any>({
      query: query => ({
        url: `/warehouse-master/wave-release${parseQueryParams(query)}`,
        method: 'GET',
      }),
      providesTags: ['WaveRelease'],
    }),

    createWaveRelease: builder.mutation<BackendResponse<any>, any>({
      query: payload => ({
        url: `/warehouse-master/wave-release`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: (_, error) => (error ? [] : ['WaveRelease']),
    }),

    updateWaveRelease: builder.mutation<BackendResponse<void>, { id: number;[key: string]: any }>({
      query: ({ id, ...rest }) => ({
        url: `/warehouse-master/wave-release/${id}`,
        method: 'PATCH',
        body: rest,
      }),
      invalidatesTags: (_, error) => (error ? [] : ['WaveRelease']),
    }),

    deleteWaveRelease: builder.mutation<BackendResponse<void>, number[] | string[] | number | string>({
      query: ids => ({
        url: `/warehouse-master/wave-release`,
        method: 'DELETE',
        body: { ids },
      }),
      invalidatesTags: (_, error) => (error ? [] : ['WaveRelease']),
    }),

    // ================= NODE TYPE =================
    getNodeType: builder.query<BackendResponse<{ count: number; list: any[] }>, any>({
      query: query => ({
        url: `/warehouse-master/node-type${parseQueryParams(query)}`,
        method: 'GET',
      }),
      providesTags: ['NodeType'],
    }),

    createNodeType: builder.mutation<BackendResponse<any>, any>({
      query: payload => ({
        url: `/warehouse-master/node-type`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: (_, error) => (error ? [] : ['NodeType']),
    }),

    updateNodeType: builder.mutation<BackendResponse<void>, { id: number;[key: string]: any }>({
      query: ({ id, ...rest }) => ({
        url: `/warehouse-master/node-type/${id}`,
        method: 'PATCH',
        body: rest,
      }),
      invalidatesTags: (_, error) => (error ? [] : ['NodeType']),
    }),

    deleteNodeType: builder.mutation<BackendResponse<void>, number[] | string[] | number | string>({
      query: ids => ({
        url: `/warehouse-master/node-type`,
        method: 'DELETE',
        body: { ids },
      }),
      invalidatesTags: (_, error) => (error ? [] : ['NodeType']),
    }),

    // ================= ASSET =================
    getAsset: builder.query<BackendResponse<{ count: number; list: any[] }>, any>({
      query: query => ({
        url: `/warehouse-master/asset${parseQueryParams(query)}`,
        method: 'GET',
      }),
      providesTags: ['Asset'],
    }),

    searchAssets: builder.query<
      BackendResponse<any>,
      { search?: string; page?: number; pageSize?: number; limit?: number }
    >({
      query: params => ({
        url: `/warehouse-master/asset/search${parseQueryParams(params)}`,
        method: 'GET',
      }),
    }),

    getAssetById: builder.query<BackendResponse<any>, number | string>({
      query: id => ({
        url: `/warehouse-master/asset/${id}`,
        method: 'GET',
      }),
      providesTags: (_, __, id) => [{ type: 'Asset', id }],
    }),

    createAsset: builder.mutation<BackendResponse<any>, {
      name: string;
      description?: string;
      erpCode?: string;
    }>({
      query: body => ({
        url: `/warehouse-master/asset`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_, error) => (error ? [] : ['Asset']),
    }),

    updateAsset: builder.mutation<
      BackendResponse<void>,
      { id: number | string; name: string; description?: string; erpCode?: string }
    >({
      query: ({ id, ...body }) => ({
        url: `/warehouse-master/asset/${id}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_, error, { id }) =>
        error ? [] : ['Asset', { type: 'Asset', id }],
    }),

    deleteAsset: builder.mutation<BackendResponse<void>, number | string>({
      query: id => ({
        url: `/warehouse-master/asset/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_, error) => (error ? [] : ['Asset']),
    }),

    getMaterialTypeDropdown: builder.query<BackendResponse<{ label: string; value: string }[]>, void>({
      query: () => ({
        url: `/warehouse-master/material-type-dropdown`,
        method: 'GET',
      }),
    }),

    // ================= BIN CAPACITY MAPPING =================
    getBinCapacityMapping: builder.query<BackendResponse<{ count: number; list: any[] }>, any>({
      query: query => ({
        url: `/warehouse/bin-capacity${parseQueryParams(query)}`,
        method: 'GET',
      }),
      providesTags: ['BinCapacityMapping'],
    }),

    getBinCapacityMappingById: builder.query<BackendResponse<any>, number | string>({
      query: id => ({
        url: `/warehouse/bin-capacity/${id}`,
        method: 'GET',
      }),
      providesTags: (_result, _error, id) => [{ type: 'BinCapacityMapping', id }],
    }),

    createBinCapacityMapping: builder.mutation<BackendResponse<any>, any>({
      query: payload => ({
        url: `/warehouse/bin-capacity`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: (_, error) => (error ? [] : ['BinCapacityMapping']),
    }),

    updateBinCapacityMapping: builder.mutation<BackendResponse<void>, { id: number | string; body: any }>({
      query: ({ id, body }) => ({
        url: `/warehouse/bin-capacity/${id}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_, error) => (error ? [] : ['BinCapacityMapping']),
    }),

    deleteBinCapacityMapping: builder.mutation<BackendResponse<void>, number | string>({
      query: id => ({
        url: `/warehouse/bin-capacity/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_, error) => (error ? [] : ['BinCapacityMapping']),
    }),

    downloadBinCapacitySample: builder.query<Blob, void>({
      query: () => ({
        url: `/warehouse/bin-capacity/sample-xlsx`,
        method: 'GET',
        responseHandler: response => response.blob(),
      }),
      transformResponse: response => {
        if (response instanceof Blob) return response;
        throw new Error('Unexpected response type');
      },
    }),

    uploadBinCapacityMapping: builder.mutation<BackendResponse<any>, FormData>({
      query: body => ({
        url: `/warehouse/bin-capacity/upload`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_, error) => (error ? [] : ['BinCapacityMapping']),
    }),

    // ================= BIN-TO-BIN TRANSFER (BT001) =================
    requestBinToBinTransfer: builder.mutation<BackendResponse<any>, { transactionId: number | string; body: any }>({
      query: ({ transactionId, body }) => ({
        url: `/warehouse-transaction/${transactionId}/request-bbt`,
        method: 'POST',
        body,
      }),
    }),

    confirmBinToBinTransfer: builder.mutation<BackendResponse<any>, { transactionId: number | string; body: any }>({
      query: ({ transactionId, body }) => ({
        url: `/warehouse-transaction/${transactionId}/confirm-bbt`,
        method: 'POST',
        body,
      }),
    }),

    // Inventory Report APIs
    getWarehouseInventoryReport: builder.query<BackendResponse<any>, any>({
      query: params => ({
        url: `/warehouse-reports/inventory${parseQueryParams(params)}`,
        method: 'GET',
      }),
    }),

    // ================= ZONE GROUP MAPPING =================
    getZoneGroupMapping: builder.query<BackendResponse<{ count: number; list: any[] }>, any>({
      query: query => ({
        url: `/warehouse-master/zone-group-mapping${parseQueryParams(query)}`,
        method: 'GET',
      }),
      providesTags: ['ZoneGroupMapping'],
    }),

    getZoneGroupMappingById: builder.query<BackendResponse<any>, number | string>({
      query: id => ({
        url: `/warehouse-master/zone-group-mapping/${id}`,
        method: 'GET',
      }),
      providesTags: (_result, _error, id) => [{ type: 'ZoneGroupMapping', id }],
    }),

    createZoneGroupMapping: builder.mutation<BackendResponse<any>, any>({
      query: payload => ({
        url: `/warehouse-master/zone-group-mapping`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: (_, error) => (error ? [] : ['ZoneGroupMapping']),
    }),

    updateZoneGroupMapping: builder.mutation<BackendResponse<void>, { id: number | string;[key: string]: any }>({
      query: ({ id, ...body }) => ({
        url: `/warehouse-master/zone-group-mapping/${id}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_, error) => (error ? [] : ['ZoneGroupMapping']),
    }),

    deleteZoneGroupMapping: builder.mutation<BackendResponse<void>, number[] | string[] | number | string>({
      query: ids => ({
        url: `/warehouse-master/zone-group-mapping`,
        method: 'DELETE',
        body: { ids },
      }),
      invalidatesTags: (_, error) => (error ? [] : ['ZoneGroupMapping']),
    }),

    // ================= WAREHOUSE ITEM MAPPING =================
    getWarehouseItemMapping: builder.query<BackendResponse<{ count: number; list: any[]; rows?: any[] }>, any>({
      query: query => ({
        url: `/warehouse-master/item-mapping${parseQueryParams(query)}`,
        method: 'GET',
      }),
      providesTags: ['WarehouseItemMapping'],
    }),

    getWarehouseItemMappingById: builder.query<BackendResponse<any>, number | string>({
      query: id => ({
        url: `/warehouse-master/item-mapping/${id}`,
        method: 'GET',
      }),
      providesTags: (_result, _error, id) => [{ type: 'WarehouseItemMapping', id }],
    }),

    createWarehouseItemMapping: builder.mutation<BackendResponse<any>, any>({
      query: payload => ({
        url: `/warehouse-master/item-mapping`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: (_, error) => (error ? [] : ['WarehouseItemMapping']),
    }),

    updateWarehouseItemMapping: builder.mutation<BackendResponse<void>, { id: number | string;[key: string]: any }>({
      query: ({ id, ...body }) => ({
        url: `/warehouse-master/item-mapping/${id}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_, error) => (error ? [] : ['WarehouseItemMapping']),
    }),

    deleteWarehouseItemMapping: builder.mutation<BackendResponse<void>, number[] | string[] | number | string>({
      query: ids => ({
        url: `/warehouse-master/item-mapping`,
        method: 'DELETE',
        body: { ids },
      }),
      invalidatesTags: (_, error) => (error ? [] : ['WarehouseItemMapping']),
    }),

    exportWarehouseInventoryReport: builder.query<Blob, any>({
      query: params => ({
        url: `/warehouse-reports/inventory/export${parseQueryParams(params)}`,
        method: 'GET',
        responseHandler: async response => {
          if (response.ok) return response.blob();
          const errorBody = await response.json().catch(() => ({}));
          throw new Error(errorBody?.message || 'Failed to export inventory report');
        },
      }),
      transformResponse: response => {
        if (response instanceof Blob) return response;
        throw new Error('Unexpected response type');
      },
    }),
  }),
});

export const {
  useGetWarehouseHierarchyQuery,
  useCreateWarehouseNodeMutation,
  useUploadWarehouseNodesCsvMutation,
  useLazyDownloadWarehouseStructureSampleQuery,
  useGetWarehouseMasterQuery,
  useGetWarehouseDropdownQuery,
  useGetWarehouseListOnUserQuery,
  useDeleteWarehouseNodeMutation,
  useUpdateWarehouseNodeMutation,
  useGetWarehouseBinDetailsQuery,
  useGetAccessTypeFieldQuery,
  useCreateAccessTypeFieldMutation,
  useDeleteAccessTypeFieldMutation,
  useUpdateAccessTypeFieldMutation,
  useGetInventoryCategoryFieldQuery,
  useCreateInventoryCategoryFieldMutation,
  useDeleteInventoryCategoryFieldMutation,
  useUpdateInventoryCategoryFieldMutation,
  useGetVehicleTypeFieldQuery,
  useCreateVehicleTypeFieldMutation,
  useDeleteVehicleTypeFieldMutation,
  useUpdateVehicleTypeFieldMutation,
  useGetTransactionTypeFieldQuery,
  useCreateTransactionTypeFieldMutation,
  useDeleteTransactionTypeFieldMutation,
  useUpdateTransactionTypeFieldMutation,
  useGetTransactionSubTypeFieldQuery,
  useGetTransactionSubTypeReferenceDocumentTypesDropdownQuery,
  useCreateTransactionSubTypeFieldMutation,
  useDeleteTransactionSubTypeFieldMutation,
  useUpdateTransactionSubTypeFieldMutation,
  useGetTransactionCompanyMappingFieldQuery,
  useCreateTransactionCompanyMappingFieldMutation,
  useDeleteTransactionCompanyMappingFieldMutation,
  useUpdateTransactionCompanyMappingFieldMutation,
  useGetWarehouseUniversalFieldsQuery,
  useCreateWarehouseUniversalFieldMutation,
  useUpdateWarehouseUniversalFieldMutation,
  useDeleteWarehouseUniversalFieldsMutation,
  useGetWarehouseFormFieldMappingsQuery,
  useGetWarehouseFormFieldMappingByIdQuery,
  useCreateWarehouseFormFieldMappingMutation,
  useCreateWarehouseFormFieldMappingWithReferenceMutation,
  useUpdateWarehouseFormFieldMappingMutation,
  useDeleteWarehouseFormFieldMappingsMutation,
  useGetWarehouseTransactionsQuery,
  useGetWarehouseTransactionByIdQuery,
  useCreateWarehouseTransactionDraftMutation,
  useUpdateWarehouseTransactionDraftMutation,
  useUpdateWarehouseTransactionDraftSilentlyMutation,
  useLazyCheckWarehouseTransactionRequestNoQuery,
  useFinalSubmitWarehouseTransactionMutation,
  useUpdateWarehouseTransactionGatePassExitMutation,
  useDeleteWarehouseTransactionMutation,
  useGetWarehouseTransactionPoDetailsQuery,
  useLazyGetWarehouseTransactionPoDetailsQuery,
  useLazySearchWarehouseRefDocumentQuery,
  useLazyExportWarehouseRefDocumentQuery,
  useSearchWarehouseBatchNoQuery,
  useLazySearchWarehouseBatchNoQuery,
  useLazyExportWarehouseBatchNoQuery,
  useUpdateWarehouseTransactionLineItemMutation,
  useDeleteWarehouseTransactionLineItemMutation,
  useGenerateWarehouseTransactionPutawayProposalMutation,
  useUpdateWarehouseTransactionPutawayProposalMutation,
  useConfirmWarehouseTransactionPutawayMutation,
  useLazyGetWarehouseTransactionPutawayProposalQuery,
  useLazyPrintWarehouseTransactionPutawayProposalQuery,
  useGenerateWarehouseTransactionBatchNoMutation,
  useGenerateWarehouseTransactionSerialNoMutation,
  useLazyGetWarehouseTransactionSerialNoQuery,
  useLazyDownloadExternalWarehouseTransactionSerialNoSampleQuery,
  useUploadExternalWarehouseTransactionSerialNoMutation,
  useGetVendorManagementAllQuery,
  useGetContractRegionQuery,
  useGetOrderReasonQuery,
  useGetUserNodeMappingQuery,
  useCreateUserNodeMappingMutation,
  useUpdateUserNodeMappingMutation,
  useDeleteUserNodeMappingMutation,
  useGetNumberRangeQuery,
  useCreateNumberRangeMutation,
  useUpdateNumberRangeMutation,
  useDeleteNumberRangeMutation,
  useGetBatchNumberRangeQuery,
  useCreateBatchNumberRangeMutation,
  useUpdateBatchNumberRangeMutation,
  useDeleteBatchNumberRangeMutation,
  useGetSerialNumberRangeQuery,
  useCreateSerialNumberRangeMutation,
  useUpdateSerialNumberRangeMutation,
  useDeleteSerialNumberRangeMutation,
  useGetWaveReleaseQuery,
  useCreateWaveReleaseMutation,
  useUpdateWaveReleaseMutation,
  useDeleteWaveReleaseMutation,
  useGetNodeTypeQuery,
  useCreateNodeTypeMutation,
  useUpdateNodeTypeMutation,
  useDeleteNodeTypeMutation,
  useGetAssetQuery,
  useGetAssetByIdQuery,
  useSearchAssetsQuery,
  useLazySearchAssetsQuery,
  useCreateAssetMutation,
  useUpdateAssetMutation,
  useDeleteAssetMutation,
  useGetMaterialTypeDropdownQuery,
  useGetBinCapacityMappingQuery,
  useGetBinCapacityMappingByIdQuery,
  useCreateBinCapacityMappingMutation,
  useUpdateBinCapacityMappingMutation,
  useDeleteBinCapacityMappingMutation,
  useLazyDownloadBinCapacitySampleQuery,
  useUploadBinCapacityMappingMutation,
  useGetWarehouseInventoryReportQuery,
  useLazyExportWarehouseInventoryReportQuery,
  useRequestBinToBinTransferMutation,
  useConfirmBinToBinTransferMutation,
  useGetZoneGroupMappingQuery,
  useGetZoneGroupMappingByIdQuery,
  useCreateZoneGroupMappingMutation,
  useUpdateZoneGroupMappingMutation,
  useDeleteZoneGroupMappingMutation,
  useGetWarehouseItemMappingQuery,
  useGetWarehouseItemMappingByIdQuery,
  useCreateWarehouseItemMappingMutation,
  useUpdateWarehouseItemMappingMutation,
  useDeleteWarehouseItemMappingMutation,
} = warehouseApi;

export default warehouseApi;
